import React, { useState } from 'react';
import { Search, Filter } from 'lucide-react';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { useTranslation } from '../../TranslationContext';

// Custom color palette - matching the Navbar
const colors = {
  white: '#ffffff',
  roseRed: '#c06a70', // Rose-red
  lightBeige: '#f5f5dc', // Light beige
  blueGray: '#879bae', // Soft blue-gray
  darkText: '#333333',
  lightText: '#ffffff'
};

interface SearchResult {
  id: string;
  title: string;
  description: string;
  category: string;
}

export const SearchPage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  const categories = ['all', 'Application', 'users', 'Articles'];

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate a search (replace with your actual API)
    setTimeout(() => {
      const mockResults: SearchResult[] = [
        {
          id: '1',
          title: t('search_result_tech_company_title'),
          description: t('search_result_tech_company_description'),
          category: t('category_articles'),
        },
        {
          id: '2',
          title: t('search_result_ibro_title'),
          description: t('search_result_ibro_description'),
          category: t('category_users'),
        },
        {
          id: '3',
          title: t('search_result_project_proposal_title'),
          description: t('search_result_project_proposal_description'),
          category: t('category_documents'),
        },
      ];

      setResults(mockResults.filter(
        (result) => selectedCategory === 'all' || result.category === t(`category_${selectedCategory}`)
      ));
      setIsLoading(false);
    }, 500);
  };

  return (
    <div>
      <Navbar />
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: colors.lightBeige }}>
        <div className="max-w-3xl mx-auto">
          <div className="bg-white shadow-sm rounded-lg p-6">
            <h1 className="text-2xl font-bold mb-6" style={{ color: colors.roseRed }}>
              {t('advanced_search')}
            </h1>

            <form onSubmit={handleSearch} className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search_placeholder')}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                  style={{ 
                    borderColor: colors.blueGray,
                    boxShadow: `0 0 0 0 ${colors.roseRed}`,
                    // ':focus': { 
                    //   boxShadow: `0 0 0 2px ${colors.roseRed}`,
                    //   borderColor: colors.roseRed 
                    // }
                  }}
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5" style={{ color: colors.blueGray }} />
              </div>

              <div className="flex items-center space-x-4">
                <Filter className="h-5 w-5" style={{ color: colors.blueGray }} />
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setSelectedCategory(category)}
                      className="px-4 py-2 rounded-md text-sm font-medium"
                      style={{ 
                        backgroundColor: selectedCategory === category ? colors.roseRed : '#f3f4f6',
                        color: selectedCategory === category ? colors.white : colors.darkText,
                      }}
                    >
                      {t(`category_${category}`)}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="w-full py-2 px-4 rounded-md focus:outline-none transition-colors duration-200"
                style={{ 
                  backgroundColor: colors.roseRed,
                  color: colors.white,
                  // ':hover': { filter: 'brightness(90%)' }
                }}
              >
                {t('search_button')}
              </button>
            </form>

            {isLoading ? (
              <div className="mt-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: colors.roseRed }}></div>
                <p style={{ color: colors.blueGray }}>{t('loading')}</p>
              </div>
            ) : (
              results.length > 0 && (
                <div className="mt-8 space-y-4">
                  {results.map((result) => (
                    <div
                      key={result.id}
                      className="p-4 border rounded-lg hover:bg-gray-50"
                      style={{ borderColor: colors.blueGray }}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium" style={{ color: colors.darkText }}>{result.title}</h3>
                        <span className="px-2 py-1 text-xs font-medium rounded-full"
                          style={{ 
                            backgroundColor: `${colors.roseRed}20`,
                            color: colors.roseRed
                          }}
                        >
                          {result.category}
                        </span>
                      </div>
                      <p className="mt-1 text-sm" style={{ color: colors.blueGray }}>{result.description}</p>
                    </div>
                  ))}
                </div>
              )
            )}

            {!isLoading && searchQuery && results.length === 0 && (
              <div className="mt-8 text-center" style={{ color: colors.blueGray }}>
                {/* {t('no_results_found' )} */}
                {t('no_results_found', { query: searchQuery })}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SearchPage;
